import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { ServiceValueMode, formatServiceDurationLabel, formatServicePriceLabel, normalizeServiceValueMode } from '../../utils/serviceValueFormat';
import { getLocalizedValue } from '../../constants/Languages';
import { getServiceName } from '../../utils/serviceTranslations';
import { PrepaymentMode, normalizePrepaymentMode } from '../../constants/PrepaymentMode';
import { resolveDeposit } from '../../utils/prepaymentUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const isPrimaryService = (service) => service.type === 'Primary';

const ServiceCard = ({
  service,
  currency,
  loading,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  // Free stores are paid in store, so a deposit saved on Pro is not asked for.
  showPrepayment = true,
}) => {
  const { t, i18n } = useTranslation();
  const localizedName = getServiceName(service.name, i18n.language);
  const localizedDescription = getLocalizedValue(
    service.description,
    i18n.language,
    undefined,
    ''
  );
  const primaryService = isPrimaryService(service);
  const isActive = !service.disabled;
  const prepaymentMode = normalizePrepaymentMode(service.prepaymentMode);
  const depositAmount = resolveDeposit(service.price, service.depositType, service.depositValue);
  const prepaymentLabel = !showPrepayment
    ? null
    : prepaymentMode === PrepaymentMode.FullPrepayment
    ? t('servicesManagement.card.fullPrepayment')
    : prepaymentMode === PrepaymentMode.Deposit && depositAmount > 0
      ? t('servicesManagement.card.deposit', { amount: formatCurrency(depositAmount, currency) })
      : null;
  const hasActions = canEdit || canDelete;

  const renderActionButtons = () => (
    <>
      {canEdit ? (
        <Tooltip title={t('servicesManagement.actions.edit')}>
          <IconButton size="small" onClick={onEdit} disabled={loading} sx={{ ...actionButtonSx, color: '#1976d2' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canDelete ? (
        <Tooltip title={t('servicesManagement.actions.delete')}>
          <IconButton size="small" onClick={onDelete} disabled={loading} sx={{ ...actionButtonSx, color: '#d32f2f' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, wordBreak: 'break-word', lineHeight: 1.3 }}>
            {localizedName}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {localizedDescription}
          </Typography>
        </Box>

        <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: hasActions ? 0.75 : 0 }}>
            <Chip
              size="small"
              label={primaryService ? t('servicesManagement.card.primaryService') : t('servicesManagement.card.secondaryService')}
              sx={{
                backgroundColor: primaryService ? '#e3f2fd' : '#ede7f6',
                color: primaryService ? '#1565c0' : '#5e35b1',
              }}
            />
            {!primaryService ? (
              <Chip
                size="small"
                label={service.isStandalone ? t('servicesManagement.bookingRule.standalone') : t('servicesManagement.bookingRule.requiresPrimary')}
                sx={{
                  backgroundColor: service.isStandalone ? '#e8f5e9' : '#fff3e0',
                  color: service.isStandalone ? '#2e7d32' : '#ef6c00',
                }}
              />
            ) : null}
            <Chip
              size="small"
              label={isActive ? t('servicesManagement.status.active') : t('servicesManagement.status.inactive')}
              sx={{
                backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                color: isActive ? '#2e7d32' : '#c62828',
              }}
            />
            {prepaymentLabel ? (
              <Chip
                size="small"
                label={prepaymentLabel}
                sx={{ backgroundColor: brandAlpha('accent', 0.12), color: BRAND.accentText }}
              />
            ) : null}
            {normalizeServiceValueMode(service.priceMode) === ServiceValueMode.Fixed && Number(service.price) === 0 && Number(service.usageLimit) >= 1 ? (
              <Chip
                size="small"
                label={t('servicesManagement.card.usageLimit', { count: service.usageLimit })}
                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
              />
            ) : null}
            {service.visibleToCustomers === false ? (
              <Chip
                size="small"
                label={t('servicesManagement.card.hiddenFromCustomers')}
                sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
              />
            ) : null}
        </Box>

        {hasActions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderActionButtons()}
          </Box>
        ) : null}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, pt: 1.5, mt: 1.5, borderTop: `1px solid ${BRAND.line}` }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('servicesManagement.card.duration')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatServiceDurationLabel(service, { onRequestLabel: t('servicesManagement.valueMode.onRequest') })}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('servicesManagement.card.price')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatServicePriceLabel(service, {
                formatMoney: (amount) => formatCurrency(amount, currency),
                onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                freeLabel: t('servicesManagement.card.free'),
                fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
              })}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ServiceCard;